import { useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Empty, fmtDate } from "@/components/ui-kit";
import { ClassPicker } from "@/components/ClassPicker";
import { Pager, fmtTime } from "@/components/Pager";
import { DayStrip } from "@/components/DayStrip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Megaphone, Trash2, Send } from "lucide-react";

export const CATS = { holiday: ["Holiday notice", "bg-emerald-100 text-emerald-800"], event: ["School event", "bg-pink-100 text-pink-800"], parent_meeting: ["Parent meeting", "bg-sky-100 text-sky-800"],
  emergency: ["Emergency", "bg-rose-600 text-white"], fee_reminder: ["Fee reminder", "bg-amber-100 text-amber-800"], exam_schedule: ["Exam schedule", "bg-violet-100 text-violet-800"],
  activity_schedule: ["Activity schedule", "bg-orange-100 text-orange-800"], general: ["General", "bg-slate-100 text-slate-700"] };
const TARGETS = { everyone: "Everyone in branch", all_parents: "All parents", class: "Parents of a class", students: "Parents of specific students", all_teachers: "All teachers", staff: "Specific staff members" };
const ALL = "__all__";

function PickList({ items, value, onChange, testid }) {
  const [q, setQ] = useState("");
  const shown = items.filter((x) => x.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="border border-violet-100 rounded-xl p-2 space-y-2">
      <Input data-testid={`${testid}-search`} placeholder="Search" value={q} onChange={(e) => setQ(e.target.value)} className="h-9" />
      <div className="max-h-48 overflow-y-auto grid sm:grid-cols-2 gap-1">
        {shown.map((x) => (
          <label key={x.id} className="flex items-center gap-2 text-sm px-2 py-1 rounded hover:bg-violet-50 cursor-pointer">
            <Checkbox data-testid={`${testid}-${x.id}`} checked={value.includes(x.id)} onCheckedChange={(v) => onChange(v ? [...value, x.id] : value.filter((i) => i !== x.id))} />
            <span className="truncate">{x.name}</span><span className="text-xs text-slate-400 truncate">{x.sub}</span>
          </label>
        ))}
      </div>
      <div className="text-xs text-slate-500">{value.length} selected</div>
    </div>
  );
}

function Composer({ onDone }) {
  const { user } = useAuth();
  const global = ["master_admin", "super_admin"].includes(user.role);
  const [branches, setBranches] = useState([]);
  const [f, setF] = useState({ branch_id: user.branch_id || "", category: "general", title: "", body: "", event_date: "", type: "all_parents" });
  const [cls, setCls] = useState({ branch_id: "", class_name: "", section: "" });
  const [students, setStudents] = useState([]);
  const [staff, setStaff] = useState([]);
  const [picked, setPicked] = useState([]);
  useEffect(() => { api.get("/branches").then((r) => { setBranches(r.data); if (!f.branch_id && r.data[0]) setF((x) => ({ ...x, branch_id: r.data[0].id })); }).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    setPicked([]);
    if (f.type === "students" && f.branch_id) api.get("/students", { params: { branch_id: f.branch_id } }).then((r) => setStudents((r.data.items || r.data).map((s) => ({ id: s.id, name: s.name, sub: `${s.class_name}-${s.section}` })))).catch(() => {});
    if (f.type === "staff") api.get("/staff").then((r) => setStaff(r.data.filter((u) => u.branch_id === f.branch_id && u.id !== user.id).map((u) => ({ id: u.id, name: u.name, sub: u.role.replace("_", " ") })))).catch(() => {});
  }, [f.type, f.branch_id]); // eslint-disable-line react-hooks/exhaustive-deps
  async function publish() {
    const target = { type: f.type, ...(f.type === "class" ? { class_name: cls.class_name, section: cls.section || null } : {}), ...(f.type === "students" ? { student_ids: picked } : {}), ...(f.type === "staff" ? { staff_ids: picked } : {}) };
    try {
      const { data } = await api.post("/announcements", { branch_id: f.branch_id, category: f.category, title: f.title, body: f.body, event_date: f.event_date || null, target });
      toast.success(`Published to ${data.recipient_count} people`); setF({ ...f, title: "", body: "", event_date: "" }); setPicked([]); onDone();
    } catch (e) { toast.error(formatApiError(e)); }
  }
  const targetOk = f.type === "class" ? cls.class_name : ["students", "staff"].includes(f.type) ? picked.length : true;
  return (
    <div data-testid="notice-form" className="bg-white rounded-2xl shadow-sm p-5 mb-6 space-y-3">
      <h3 className="font-display font-bold text-slate-900">Publish an announcement</h3>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div><Label>Branch</Label>
          <Select value={f.branch_id} onValueChange={(v) => setF({ ...f, branch_id: v })} disabled={!global}>
            <SelectTrigger data-testid="notice-branch" className="mt-1"><SelectValue placeholder="Branch" /></SelectTrigger>
            <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label>Type</Label>
          <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
            <SelectTrigger data-testid="notice-category" className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(CATS).map(([k, [l]]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label>Send to</Label>
          <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
            <SelectTrigger data-testid="notice-target" className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(TARGETS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select></div>
      </div>
      <div><Label>Date (optional)</Label><div className="mt-1"><DayStrip testid="notice-date" value={f.event_date} onChange={(v) => setF({ ...f, event_date: v })} clearLabel="No date" /></div></div>
      {f.type === "class" && <ClassPicker value={{ ...cls, branch_id: f.branch_id }} onChange={(v) => setCls(v)} allowAllSections />}
      {f.type === "students" && <PickList items={students} value={picked} onChange={setPicked} testid="notice-student" />}
      {f.type === "staff" && <PickList items={staff} value={picked} onChange={setPicked} testid="notice-staff" />}
      <div><Label>Title</Label><Input data-testid="notice-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="mt-1" /></div>
      <div><Label>Message</Label><Textarea data-testid="notice-body" rows={3} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} className="mt-1" /></div>
      <Button data-testid="notice-publish" onClick={publish} disabled={!f.branch_id || f.title.length < 2 || f.body.length < 2 || !targetOk} className="btn-brand"><Send size={15} className="mr-1" /> Publish & notify</Button>
    </div>
  );
}

export default function NoticesPage() {
  const { user } = useAuth();
  const canPost = ["master_admin", "super_admin", "principal"].includes(user?.role);
  const [cat, setCat] = useState(ALL);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [day, setDay] = useState("");
  const [markedDays, setMarkedDays] = useState([]);
  const [d, setD] = useState({ items: [], page: 1, pages: 1, total: 0 });
  const load = () => api.get("/announcements", { params: { page, q, category: cat === ALL ? undefined : cat, day: day || undefined } }).then((r) => setD(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { api.get("/announcements/dates").then((r) => setMarkedDays(r.data)).catch(() => {}); }, [d.total]);
  useEffect(() => { load(); }, [page, q, cat, day]); // eslint-disable-line react-hooks/exhaustive-deps
  async function del(a) { if (!window.confirm("Delete this announcement?")) return; try { await api.delete(`/announcements/${a.id}`); load(); } catch (e) { toast.error(formatApiError(e)); } }
  return (
    <AppShell>
      <PageHeader eyebrow="Notice board" title="School Announcements" subtitle={canPost ? "Publish holiday notices, meetings, exams and emergencies to exactly the right people." : "Announcements meant for you."} />
      {canPost && <Composer onDone={load} />}
      <div className="mb-4"><DayStrip testid="notice-day" marked={markedDays} countLabel="announcements"
        preview={(dt) => api.get("/announcements", { params: { day: dt, limit: 50 } }).then((r) => r.data.items.map((a) => ({ id: a.id, title: a.title, sub: CATS[a.category]?.[0] })))} value={day} onChange={(v) => { setDay(v); setPage(1); }} clearLabel="All dates" /></div>
      <div className="flex flex-wrap gap-3 mb-4">
        <Select value={cat} onValueChange={(v) => { setCat(v); setPage(1); }}>
          <SelectTrigger data-testid="notice-filter-cat" className="w-48 bg-white"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>All types</SelectItem>{Object.entries(CATS).map(([k, [l]]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <Input data-testid="notice-search" placeholder="Search announcements" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} className="w-60 bg-white" />
      </div>
      {d.items.length === 0 ? <Empty testid="notice-empty" icon={Megaphone} text="No announcements yet." /> : (
        <div className="space-y-3">{d.items.map((a) => (
          <div key={a.id} data-testid={`notice-${a.id}`} className={`bg-white rounded-2xl shadow-sm p-4 ${a.category === "emergency" ? "border-l-4 border-rose-600" : ""}`}>
            <div className="flex flex-wrap items-center gap-2">
              <Badge className={CATS[a.category]?.[1]}>{CATS[a.category]?.[0]}</Badge>
              <span className="font-display font-bold text-slate-900">{a.title}</span>
              {a.event_date && <span className="text-xs font-semibold text-violet-700">{fmtDate(a.event_date)}</span>}
              {canPost && <button data-testid={`notice-delete-${a.id}`} onClick={() => del(a)} className="ml-auto text-slate-400 hover:text-red-600"><Trash2 size={15} /></button>}
            </div>
            <p className="text-sm text-slate-700 mt-2 whitespace-pre-line">{a.body}</p>
            <div className="text-xs text-slate-400 mt-2">{a.created_by_name} • {fmtTime(a.created_at)}{canPost ? ` • ${TARGETS[a.target.type]}${a.target.class_name ? ` (${a.target.class_name}${a.target.section ? `-${a.target.section}` : ""})` : ""} • ${a.recipient_count} recipients` : ""}</div>
          </div>
        ))}</div>
      )}
      <Pager page={d.page} pages={d.pages} total={d.total} onPage={setPage} testid="notice-pager" />
    </AppShell>
  );
}
